// Channel registry helpers (SPEC F6): validate an imported channels.json,
// export it in the repo's format, and merge the shipped registry with local edits.

import { STYLE_IDS, type Channel, type StyleId } from '../data/types';
import { validateChannel, validateRegistry, type RegistryIssue } from './channel-rules';
import type { CrateChannel } from './dig';

export const CHANNELS_README =
  'Add YouTube channels that upload full records. id = channel ID starting with UC (or leave empty and set handle). styles = style IDs from SPEC §6. weight 1-4 (1, 2, 4 or 8 times the chance). era = reissue, new or mixed: new channels are hidden unless Include new recordings is on.';

export interface ParsedChannels {
  channels: Channel[];
  errors: string[];
}

/** One line per problem, for the app: "Channel 3 (Name): weight must be 1, 2 or 3". */
export function issueText(issue: RegistryIssue): string {
  if (issue.index < 0) return `This file has ${issue.message}. ${issue.fix}`;
  return `Channel ${issue.index + 1}${issue.name ? ` (${issue.name})` : ''}: ${issue.message}`;
}

/** The app's import: the shared registry rules (channel-rules.ts). Accepts `{ channels: [...] }` or a bare list. Bad entries are reported and skipped, never guessed at. */
export function parseChannels(text: string): ParsedChannels {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    return { channels: [], errors: ['This file is not valid JSON. Export channels.json from Dig › Channels and try again.'] };
  }
  const { channels, errors } = validateRegistry(data, { styleIds: STYLE_IDS, allowBareList: true });
  if (errors.some((e) => e.index < 0)) return { channels: [], errors: ['No "channels" list found in this file.'] };
  return { channels: channels.map((c) => ({ ...c, styles: c.styles as StyleId[] })), errors: errors.map(issueText) };
}

/** The editor form's check, with the same rules as an import. Returns the first problem, or nothing. */
export function checkChannel(channel: Omit<Channel, 'localId'>): string | undefined {
  const { issues } = validateChannel(channel, 0, STYLE_IDS);
  const first = issues[0];
  return first ? `${first.message.charAt(0).toUpperCase()}${first.message.slice(1)}. ${first.fix}` : undefined;
}

/** The repo's `data/channels.json` format. */
export function exportChannels(channels: readonly Channel[]): string {
  const out = channels.map((c) => ({
    id: c.id ?? '', handle: c.handle, name: c.name, styles: c.styles, genres: c.genres, weight: c.weight, era: c.era ?? 'mixed', enabled: c.enabled,
  }));
  return `${JSON.stringify({ _readme: CHANNELS_README, channels: out }, null, 2)}\n`;
}

/**
 * The channels the dig uses: the shipped registry (public/crate/channels.json,
 * written by the crate build), with local edits winning by id. Local channels
 * with no id yet (a handle the build hasn't resolved) have no records, so they
 * don't take part. A local tombstone (`deleted: true`) hides a shipped channel
 * — the id can't be removed from the shipped file, so "delete" on a shipped
 * row is recorded locally instead (SPEC F6: "added or deleted channels").
 */
export function effectiveChannels(shipped: readonly CrateChannel[], local: readonly Channel[]): CrateChannel[] {
  const byId = new Map<string, CrateChannel>(shipped.map((c) => [c.id, c]));
  for (const l of local) {
    if (!l.id) continue;
    if (l.deleted) {
      byId.delete(l.id);
      continue;
    }
    byId.set(l.id, { id: l.id, name: l.name, styles: l.styles, genres: l.genres, weight: l.weight, era: l.era, enabled: l.enabled });
  }
  return [...byId.values()];
}
